import type { DataGridLeafColumn } from '@ibsys/kit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExpertReportFeedbackItem } from '@/entities/expert-report';
import { UserDataGridFilter } from '@/entities/users';
import { formatHumanDate } from '@/shared/lib';
import { TextDiff } from '@/shared/ui';
import { FEEDBACK_COLUMNS } from '../model/constants';
import { type FeedbackCellValue, formatFeedbackValue, getCommentCell, getRatingCell } from '../model/values';
import { ExpertReportFilter } from './ExpertReportFilter';
import { FeedbackConversationLink } from './FeedbackConversationLink';

/** Ячейка значения: абзацы комментария сохраняются переносами, поэтому они и рисуются как переносы */
const renderValue = (value: string) => <span className="whitespace-pre-line">{value}</span>;

/**
 * Ячейка столбца значения: изменённое правкой показывается сравнением — тем же оформлением,
 * что и в самом заключении, — а не изменённое обычным текстом.
 */
const renderCell = (cell: FeedbackCellValue) =>
  cell.kind === 'diff' ? <TextDiff previous={cell.previous} next={cell.next} /> : renderValue(cell.text);

/**
 * Колонки списка правок: отбор — по диалогу с переходом к правленому элементу, времени, автору,
 * заключению и типу элемента; сортировка — по всем столбцам, кроме значений правки.
 */
export const useExpertReportFeedbackColumns = (): DataGridLeafColumn<ExpertReportFeedbackItem>[] => {
  const { t, i18n } = useTranslation(['expertReport']);

  return useMemo(
    () => [
      {
        id: FEEDBACK_COLUMNS.conversation,
        header: t('expertReport:feedback.conversation'),
        accessor: 'conversationId',
        size: 120,
        cell: ({ row }) => <FeedbackConversationLink item={row} />,
      },
      {
        id: FEEDBACK_COLUMNS.changedAt,
        header: t('expertReport:feedback.changedAt'),
        accessor: 'changedAt',
        size: 180,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'timestamp-range' },
        cell: ({ row }) => formatHumanDate(i18n, row.changedAt, { withTime: true }),
      },
      {
        id: FEEDBACK_COLUMNS.author,
        header: t('expertReport:feedback.author'),
        accessor: 'authorName',
        size: 200,
        enableSorting: true,
        enableFiltering: true,
        renderFilter: (ctx) => <UserDataGridFilter ctx={ctx} />,
      },
      {
        id: FEEDBACK_COLUMNS.version,
        header: t('expertReport:feedback.version'),
        accessor: 'version',
        size: 90,
        align: 'right',
        enableSorting: true,
      },
      {
        id: FEEDBACK_COLUMNS.target,
        header: t('expertReport:feedback.target'),
        accessor: 'blockName',
        size: 240,
        enableSorting: true,
        enableFiltering: true,
        filter: {
          type: 'select',
          options: [
            { value: 'block', label: t('expertReport:feedback.targetBlock') },
            { value: 'rule', label: t('expertReport:feedback.targetRule') },
          ],
        },
        // Правка блока приходит вместе с правилом, чей комментарий в неё попал
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span>{row.blockName}</span>
            {row.ruleName && <span className="text-xs text-muted-foreground">{row.ruleName}</span>}
          </span>
        ),
      },
      {
        id: FEEDBACK_COLUMNS.rating,
        header: t('expertReport:feedback.rating'),
        size: 160,
        wrap: true,
        cell: ({ row }) => renderCell(getRatingCell(row)),
      },
      {
        id: FEEDBACK_COLUMNS.comment,
        header: t('expertReport:feedback.comment'),
        size: 360,
        wrap: true,
        cell: ({ row }) => renderCell(getCommentCell(row)),
      },
      {
        id: FEEDBACK_COLUMNS.ratingPreviousValue,
        header: t('expertReport:feedback.ratingPreviousValue'),
        size: 140,
        wrap: true,
        cell: ({ row }) => renderValue(formatFeedbackValue(row.ratingPreviousValue)),
      },
      {
        id: FEEDBACK_COLUMNS.ratingValue,
        header: t('expertReport:feedback.ratingValue'),
        size: 140,
        wrap: true,
        // Пустая оценка правки — возврат к рассчитанной системой, поэтому показываем именно её
        cell: ({ row }) =>
          row.ratingValue === null ? (
            <span>
              {renderValue(formatFeedbackValue(row.ratingSystemValue))}{' '}
              <span className="text-muted-foreground">({t('expertReport:feedback.backToSystemValue')})</span>
            </span>
          ) : (
            renderValue(formatFeedbackValue(row.ratingValue))
          ),
      },
      {
        id: FEEDBACK_COLUMNS.commentPreviousValue,
        header: t('expertReport:feedback.commentPreviousValue'),
        size: 260,
        wrap: true,
        cell: ({ row }) => renderValue(formatFeedbackValue(row.commentPreviousValue)),
      },
      {
        id: FEEDBACK_COLUMNS.commentValue,
        header: t('expertReport:feedback.commentValue'),
        size: 260,
        wrap: true,
        // Пустой комментарий правки — возврат к составленному системой
        cell: ({ row }) =>
          row.commentValue === null ? (
            <span>
              {renderValue(formatFeedbackValue(row.commentSystemValue))}{' '}
              <span className="text-muted-foreground">({t('expertReport:feedback.backToSystemValue')})</span>
            </span>
          ) : (
            renderValue(formatFeedbackValue(row.commentValue))
          ),
      },
      {
        id: FEEDBACK_COLUMNS.expertReport,
        header: t('expertReport:feedback.expertReport'),
        accessor: 'expertReportName',
        size: 200,
        enableSorting: true,
        enableFiltering: true,
        renderFilter: (ctx) => <ExpertReportFilter ctx={ctx} />,
      },
    ],
    [t, i18n],
  );
};
